/*----------------------------------
- ROUTE: /sales
----------------------------------*/

/**
 * @docs docs/features/sales-page
 * @adr  ADR-0067
 * @adr  ADR-0028
 * @rule Static and hourly: neither `data` nor `render` reads the query string (a static page is
 *       cached by path whatever the query), so a visitor's link carrying a sort, a filter or a
 *       page lands on the plain page. Before the first nightly rollup it answers 503.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';
import type { TFetcher } from '@common/router/request/api';
import { SALES_FULL_MARKDOWN_PATH, SALES_PAGE_MIRROR_PATH, SALES_PATH, type PublicSalesPage } from '@uniquedomains/components/contracts/sales/public';
import { buildSalesFaq } from '@uniquedomains/components/sales/salesModel';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { SALES_PAGE_CONTENT } from '@/client/catalogs/landing/sales/pageContent';
import { PublicPageUnavailable, setPublicUnavailableHeaders } from '@/client/pages/landing/lib/publicUnavailable';
import { buildSalesStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import { cachedLandingHeaderNavigation, cachedPublicSalesPage } from '@/common/libs/connected/publicProductReadCache';
import SalesLandingPage from './page/SalesLandingPage';

/*----------------------------------
- TYPES
----------------------------------*/

type SalesRouteDataContext = {
    Product: {
        Navigation: { GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation> };
        Sales: { GetPublicPage: () => TFetcher<PublicSalesPage | null> };
    };
};

type SalesRouteRenderContext = {
    sales: PublicSalesPage | null;
    navigation: LandingHeaderDomainsNavigation;
    page: {
        description?: string;
        head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
        jsonld: WebsiteJsonLdNode[];
        metas: Record<string, string | Date | null | undefined>;
        title?: string;
        url: string;
    };
    request: { url: string; res?: { setHeader: (name: string, value: string) => void } };
    response?: { setHeaders?: (headers: Record<string, string>) => void };
    Router: { url: (path: string) => string };
};

/*----------------------------------
- PAGE
----------------------------------*/

export default definePageRoute({
    path: '/sales',
    options: {
        auth: false,
        layout: false,
        // The figures move once a night; an hour bounds how late a replica shows the new night.
        static: { refresh: '1 hour', urls: ['/sales'] },
    },
    data: ({ Product }: SalesRouteDataContext) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
        sales: cachedPublicSalesPage.read() ?? Product.Sales.GetPublicPage(),
    }),
    render: ({ sales, navigation, page, request, response, Router }: SalesRouteRenderContext) => {
        if (!sales) {
            setPublicUnavailableHeaders(request, response);
            throw new PublicPageUnavailable(SALES_PAGE_CONTENT.unavailable);
        }
        // Seeded only from a page with figures, so a pending page is retried on the next render.
        if (sales.pulse.windows.length) cachedPublicSalesPage.seed(sales);

        const { pageTitle, metaDescription, markdownTitle } = SALES_PAGE_CONTENT.meta;
        const canonicalUrl = Router.url(SALES_PATH);
        page.title = pageTitle;
        page.description = metaDescription;
        page.url = canonicalUrl;
        page.metas = {
            ...page.metas,
            'og:description': metaDescription,
            'og:title': pageTitle,
            'og:type': 'website',
            'og:url': canonicalUrl,
            'twitter:card': 'summary_large_image',
            'twitter:description': metaDescription,
            'twitter:title': pageTitle,
            'twitter:url': canonicalUrl,
        };
        page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
        page.head.push({ $: 'link', rel: 'alternate', type: 'text/markdown', title: markdownTitle, href: Router.url(SALES_FULL_MARKDOWN_PATH) });
        page.head.push({ $: 'link', rel: 'alternate', type: 'text/markdown', title: `${markdownTitle}, summary`, href: Router.url(SALES_PAGE_MIRROR_PATH) });
        page.jsonld.push(
            ...buildSalesStructuredData({
                canonicalUrl,
                description: metaDescription,
                title: pageTitle,
                markdownUrl: Router.url(SALES_FULL_MARKDOWN_PATH),
                dateModified: sales.freshness.rollupAt,
                temporalCoverage: sales.freshness.archiveSince ? `${sales.freshness.archiveSince}/${sales.freshness.today}` : null,
                faqs: buildSalesFaq(sales, SALES_PAGE_CONTENT.copy),
            }),
        );

        return <SalesLandingPage page={sales} navigation={navigation} canonicalUrl={canonicalUrl} />;
    },
});
